import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search/directory-search"
import { X } from "@/components/icons"
import { Input } from "@/components/ui/input"

import { MultiSelectDropdown } from "./multi-select-dropdown"
import { SITE_AREAS } from "../../types/use-case.types"

type FactSheetHeaderProps = {
  ucName: string
  owner: string
  siteAreaSel: string[]
  onChange: (field: string, value: string | string[]) => void
}

const siteOptions = SITE_AREAS.map((s) => ({ value: s, label: s }))

export function FactSheetHeader({ ucName, owner, siteAreaSel, onChange }: FactSheetHeaderProps) {
  const { t } = useTranslation()
  const [isEditingOwner, setIsEditingOwner] = useState(false)

  const handleOwnerSelect = (result: DirectorySearchResult) => {
    onChange("owner", result.displayName)
    setIsEditingOwner(false)
  }

  const showSearch = !owner || isEditingOwner

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.header.ucName")}
        </label>
        <Input
          placeholder={t("useCases.factSheet.header.ucNamePlaceholder")}
          value={ucName}
          onChange={(e) => onChange("ucName", e.target.value)}
        />
      </div>
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.header.owner")}
        </label>
        {showSearch ? (
          <DirectorySearch
            placeholder={t("useCases.factSheet.header.ownerPlaceholder")}
            types={["users"]}
            onSelect={handleOwnerSelect}
          />
        ) : (
          <div className="border-input flex h-9 items-center justify-between rounded-md border px-3 py-1">
            <span className="truncate text-sm">{owner}</span>
            <button
              className="text-muted-foreground hover:text-foreground ml-2 shrink-0"
              type="button"
              onClick={() => {
                onChange("owner", "")
                setIsEditingOwner(true)
              }}
            >
              <X className="size-4" />
            </button>
          </div>
        )}
      </div>
      <div className="bg-card rounded-lg border p-3 shadow-sm">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold tracking-wide uppercase">
          {t("useCases.factSheet.header.siteArea")}
        </label>
        <MultiSelectDropdown
          options={siteOptions}
          placeholder="Select sites..."
          selected={siteAreaSel}
          onChange={(v) => onChange("siteAreaSel", v)}
        />
      </div>
    </div>
  )
}
